import {
  type AocSnapshot,
  type CoreSnapshot,
  type DebugStopReason,
  type DebuggerRuleInfo,
  type DicSnapshot,
  type IocSnapshot,
  type TicSnapshot,
  type VocSnapshot
} from "./emulator.js";
import type { DebuggerElements } from "./debugger.js";

export type MachineStateTabId = "cpu" | "ioc" | "voc" | "aoc" | "tic" | "dic";

export type MachineStateTab = {
  id: MachineStateTabId;
  button: HTMLButtonElement;
  panel: HTMLElement;
};

export type MachineSnapshots = {
  ioc: IocSnapshot;
  voc: VocSnapshot;
  aoc: AocSnapshot;
  tic: TicSnapshot;
  dic: DicSnapshot;
};

type MachineField = {
  label: string;
  value: string;
};

type MachineGroup = {
  title: string;
  subtitle?: string;
  fields: MachineField[];
};

const FLAG_NEGATIVE = 0x80;
const FLAG_OVERFLOW = 0x40;
const FLAG_INTERRUPT_DISABLE = 0x04;
const FLAG_ZERO = 0x02;
const FLAG_CARRY = 0x01;
const VOC_VISIBLE_START = 48;
const VOC_VISIBLE_END = 248;
const TEXT_COLUMNS = 40;
const TEXT_ROWS = 25;
const TEXT_CHARS = TEXT_COLUMNS * TEXT_ROWS;
const SPRITE_PTR_OFFSET = TEXT_CHARS;
const AUDIO_PITCH_CLOCK_HZ = 48_000;
const AUDIO_SOURCE_RATE_HZ = 48_000;
const AUDIO_FILTER_CUTOFF_MIN_HZ = 80;
const AUDIO_FILTER_CUTOFF_MAX_HZ = 12_000;
const AUDIO_FILTER_RES_MASK = 0x0f;
const AUDIO_FILTER_MODE_MASK = 0x30;
const AUDIO_CTRL_GATE = 0x01;
const AUDIO_CTRL_SYNC = 0x02;
const AUDIO_CTRL_RING = 0x04;
const AUDIO_CTRL_TEST = 0x08;
const AUDIO_CTRL_TRI = 0x10;
const AUDIO_CTRL_SAW = 0x20;
const AUDIO_CTRL_PULSE = 0x40;
const AUDIO_CTRL_NOISE = 0x80;
const AUDIO_WAVE_MASK = AUDIO_CTRL_TRI | AUDIO_CTRL_SAW | AUDIO_CTRL_PULSE | AUDIO_CTRL_NOISE;
const AUDIO_AUX_RATE_X8 = 0x01;
const TIC_IFR_UNDERFLOW = 0x01;
const TIC_CTRL_ONE_SHOT = 0x08;
const TIC_CTRL_LOAD_STROBE = 0x10;
const TIC_CTRL_TB_COUNT_TA_UNDERFLOW = 0x40;
const IOC_KBD_STATUS_READY = 0x01;
const IOC_KBD_STATUS_OVERFLOW = 0x02;
const IOC_KBD_STATUS_IRQ = 0x80;
const IOC_ICR_SET_MASK = 0x80;
const STORAGE_STATUS_BUSY = 0x01;
const STORAGE_STATUS_DONE = 0x02;
const STORAGE_STATUS_ERR = 0x04;
const STORAGE_STATUS_MOUNTED = 0x08;
const STORAGE_STATUS_DIRTY = 0x10;
const STORAGE_STATUS_RO = 0x20;
const STORAGE_DESCRIPTOR_NAME_OFFSET = 4;
const STORAGE_DESCRIPTOR_NAME_LENGTH = 16;
const STORAGE_DESCRIPTOR_BUFFER_ADDR_OFFSET = 0x14;
const STORAGE_DESCRIPTOR_BUFFER_LEN_OFFSET = 0x16;
const STORAGE_DESCRIPTOR_XFER_LEN_OFFSET = 0x1a;
const VOC_BUS_OWNER_LABELS = ["CPU", "VOC"] as const;
const VOC_HIGH_REASON_LABELS = ["none", "sprite-data", "badline-matrix"] as const;
const AUDIO_ENV_LABELS = ["idle", "attack", "decay", "sustain", "release"] as const;
const AUDIO_PITCH_LABELS = ["coarse_16", "balanced_19", "fine_20"] as const;
const STORAGE_COMMAND_LABELS: Record<number, string> = {
  0: "IDLE",
  1: "STATUS",
  2: "DIR_FIRST",
  3: "DIR_NEXT",
  4: "LOAD_PRG",
  5: "SAVE_PRG",
  6: "DELETE",
  7: "RENAME",
  8: "SYNC",
  9: "LABEL",
  10: "SAVE_COMMIT",
  11: "DESC_PUSH",
  12: "DESC_PULL",
  13: "RENAME_NAME_PUSH"
};
const STORAGE_JOB_STATE_LABELS: Record<number, string> = {
  0: "idle",
  1: "execute"
};
const STORAGE_STREAM_MODE_LABELS: Record<number, string> = {
  0: "none",
  1: "load",
  2: "save",
  3: "desc-in",
  4: "desc-out",
  5: "rename-name"
};
const STORAGE_ERROR_LABELS: Record<number, string> = {
  0: "OK",
  1: "NOT_MOUNTED",
  2: "ALREADY_MOUNTED",
  3: "READ_ONLY",
  4: "BAD_ARG",
  5: "NAME_INVALID",
  6: "NAME_EXISTS",
  7: "NOT_FOUND",
  8: "DIR_FULL",
  9: "NO_SPACE",
  10: "BUF_TOO_SMALL",
  11: "IMAGE_FORMAT",
  12: "IMAGE_CORRUPT",
  13: "IO",
  14: "BUSY"
};

function formatHexByte(value: number): string {
  return value.toString(16).toUpperCase().padStart(2, "0");
}

export function formatHexWord(value: number): string {
  return value.toString(16).toUpperCase().padStart(4, "0");
}

function formatHexDword(value: number): string {
  return (value >>> 0).toString(16).toUpperCase().padStart(8, "0");
}

function formatHexBytePrefixed(value: number): string {
  return `0x${formatHexByte(value & 0xff)}`;
}

function formatHexWordPrefixed(value: number): string {
  return `0x${formatHexWord(value & 0xffff)}`;
}

function formatHexDwordPrefixed(value: number): string {
  return `0x${formatHexDword(value)}`;
}

function formatYesNo(value: boolean): string {
  return value ? "yes" : "no";
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatHz(value: number): string {
  if (!Number.isFinite(value)) {
    return "n/a";
  }
  if (value >= 1000) {
    return `${value.toFixed(2)} Hz`;
  }
  return `${value.toFixed(3)} Hz`;
}

function formatRatio(current: number, total: number): string {
  if (total <= 0) {
    return `${current}/${total}`;
  }
  return `${current}/${total} (${Math.round((current / total) * 100)}%)`;
}

function countBits(value: number): number {
  let bits = value & 0xff;
  let count = 0;
  while (bits !== 0) {
    count += bits & 1;
    bits >>>= 1;
  }
  return count;
}

function petsciiByteToChar(value: number): string {
  if (value === 0x00 || value === 0xa0) {
    return " ";
  }
  if (value >= 0x20 && value <= 0x7e) {
    return String.fromCharCode(value);
  }
  return ".";
}

function petsciiBytesToText(bytes: Uint8Array): string {
  const text = Array.from(bytes, petsciiByteToChar).join("").trimEnd();
  return text === "" ? "(empty)" : text;
}

function descriptorU16(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8);
}

function renderMachineGroups(container: HTMLElement, groups: MachineGroup[]): void {
  container.textContent = "";
  const fragment = document.createDocumentFragment();

  for (const group of groups) {
    if (group.fields.length === 0) {
      continue;
    }

    const card = document.createElement("article");
    card.className = "machine-state-group";

    const heading = document.createElement("div");
    heading.className = "machine-state-group__header";

    const title = document.createElement("h4");
    title.textContent = group.title;
    heading.append(title);

    if (group.subtitle !== undefined) {
      const subtitle = document.createElement("p");
      subtitle.className = "machine-state-group__meta";
      subtitle.textContent = group.subtitle;
      heading.append(subtitle);
    }

    const fieldList = document.createElement("dl");
    fieldList.className = "machine-state-fields";
    for (const field of group.fields) {
      const row = document.createElement("div");
      row.className = "machine-state-field";

      const label = document.createElement("dt");
      label.textContent = field.label;
      const value = document.createElement("dd");
      value.textContent = field.value;
      row.append(label, value);
      fieldList.append(row);
    }

    card.append(heading, fieldList);
    fragment.append(card);
  }

  container.append(fragment);
}

export function setMachineStateTabState(activeTab: MachineStateTabId, tabs: MachineStateTab[]): void {
  for (const tab of tabs) {
    const active = tab.id === activeTab;
    tab.button.setAttribute("aria-selected", active ? "true" : "false");
    tab.button.tabIndex = active ? 0 : -1;
    tab.button.classList.toggle("is-active", active);
    tab.panel.hidden = !active;
    tab.panel.classList.toggle("is-active", active);
  }
}

function renderFlag(chip: HTMLElement, set: boolean): void {
  chip.classList.toggle("is-set", set);
  chip.setAttribute("aria-pressed", set ? "true" : "false");
}

export function isPlainAddressBreakpoint(text: string): boolean {
  return /^(?:\$[0-9a-fA-F]{1,4}|0x[0-9a-fA-F]{1,4}|\d+)$/.test(text.trim());
}

function isWatchpointExpression(text: string): boolean {
  return /^on\s+(?:read|write|access)\s+mem\s*\[/i.test(text.trim());
}

function formatRuleStopMessage(rule: DebuggerRuleInfo | null, snapshot: CoreSnapshot | null): string {
  if (rule === null) {
    return snapshot === null ? "Stopped at a breakpoint." : `Stopped at breakpoint 0x${formatHexWord(snapshot.pc)}.`;
  }
  if (rule.action === "trace_stop") {
    return `Trace+Stop matched: ${rule.matchText}.`;
  }
  if (isWatchpointExpression(rule.matchText)) {
    return `Stopped at watchpoint: ${rule.matchText}.`;
  }
  if (rule.anchorAddress !== null && isPlainAddressBreakpoint(rule.matchText)) {
    return `Stopped at breakpoint 0x${formatHexWord(rule.anchorAddress)}.`;
  }
  if (rule.anchorAddress !== null) {
    return `Stopped at breakpoint 0x${formatHexWord(rule.anchorAddress)}: ${rule.matchText}.`;
  }
  return `Stopped at breakpoint: ${rule.matchText}.`;
}

export function stopReasonText(
  reason: DebugStopReason,
  snapshot: CoreSnapshot | null,
  rule: DebuggerRuleInfo | null
): string {
  switch (reason) {
    case "manual":
      return "";
    case "breakpoint":
      return formatRuleStopMessage(rule, snapshot);
    case "step":
      return snapshot === null ? "Step complete." : `Step complete at 0x${formatHexWord(snapshot.pc)}.`;
    case "halted":
      return snapshot === null ? "CPU halted." : `CPU halted after ${snapshot.cycles} cycles.`;
    default:
      return "";
  }
}

function formatPhaseLabel(phase: CoreSnapshot["phase"]): string {
  switch (phase) {
    case "FETCH":
      return "Fetching next opcode";
    case "READ_OP1":
      return "Reading first operand byte";
    case "READ_OP2":
      return "Reading second operand byte";
    case "ZPX_IDLE":
      return "Settling zero-page index";
    case "INDIRECT_PRE_IDLE":
      return "Settling pre-indexed indirect address";
    case "INDIRECT_READ_LO":
      return "Reading indirect low byte";
    case "INDIRECT_READ_HI":
      return "Reading indirect high byte";
    case "INDIRECT_POST_IDLE":
      return "Settling post-indexed indirect address";
    case "IND_ABS_READ_LO":
      return "Reading indirect absolute low byte";
    case "IND_ABS_READ_HI":
      return "Reading indirect absolute high byte";
    case "PAGECROSS_IDLE":
      return "Settling page crossing";
    case "MEM_READ":
      return "Reading memory";
    case "MEM_WRITE":
      return "Writing memory";
    case "EXEC":
      return "Executing instruction";
    case "BRANCH_EVAL":
      return "Evaluating branch";
    case "BRANCH_TAKEN_IDLE":
      return "Settling taken branch";
    case "BRANCH_PAGE_IDLE":
      return "Settling branch page crossing";
    case "PUSH_REG":
      return "Pushing register to stack";
    case "POP_REG":
      return "Popping register from stack";
    case "JSR_PUSH_PCH":
      return "JSR: pushing return high byte";
    case "JSR_PUSH_PCL":
      return "JSR: pushing return low byte";
    case "JSR_SET_PC":
      return "JSR: jumping to target";
    case "RTS_POP_PCL":
      return "RTS: popping return low byte";
    case "RTS_POP_PCH":
      return "RTS: popping return high byte";
    case "RTS_INC_PC":
      return "RTS: advancing return address";
    case "RTI_POP_P":
      return "RTI: popping status register";
    case "RTI_POP_PCL":
      return "RTI: popping PC low byte";
    case "RTI_POP_PCH":
      return "RTI: popping PC high byte";
    case "TRAP_PUSH_PCH":
      return "Trap: pushing PC high byte";
    case "TRAP_PUSH_PCL":
      return "Trap: pushing PC low byte";
    case "TRAP_PUSH_P":
      return "Trap: pushing status register";
    case "TRAP_SET_I":
      return "Trap: setting interrupt disable";
    case "TRAP_VEC_LO":
      return "Trap: reading vector low byte";
    case "TRAP_VEC_HI":
      return "Trap: reading vector high byte";
    case "HALTED":
      return "Remaining halted";
    default:
      return "Unknown phase";
  }
}

function vocIrqPendingText(irqIfr: number): string {
  const names: string[] = [];
  if ((irqIfr & 0x01) !== 0) {
    names.push("rast");
  }
  if ((irqIfr & 0x02) !== 0) {
    names.push("spr/bg");
  }
  if ((irqIfr & 0x04) !== 0) {
    names.push("spr/spr");
  }
  return names.length === 0 ? "none" : names.join(", ");
}

function vocModeLabel(ctrl1: number, ctrl2: number): string {
  const bmm = (ctrl1 & 0x20) !== 0;
  const mcm = (ctrl2 & 0x10) !== 0;
  const ecm = (ctrl1 & 0x40) !== 0;

  if (!bmm && !mcm) {
    return ecm ? "text (ECM reserved)" : "text 40x25";
  }
  if (bmm && !mcm) {
    return ecm ? "Mode A 320x200 1bpp (ECM reserved)" : "Mode A 320x200 1bpp";
  }
  if (bmm && mcm) {
    return ecm ? "Mode B 160x200 2bpp (ECM reserved)" : "Mode B 160x200 2bpp";
  }
  return ecm ? "Mode C 160x200 2bpp (ECM reserved)" : "Mode C 160x200 2bpp";
}

function vocLogicalResolutionLabel(ctrl1: number, ctrl2: number): string {
  const bmm = (ctrl1 & 0x20) !== 0;
  const mcm = (ctrl2 & 0x10) !== 0;
  if (!bmm && !mcm) {
    return "40x25 cells -> 320x200 scanout";
  }
  if (bmm && !mcm) {
    return "320x200 logical pixels";
  }
  return "160x200 logical pixels -> doubled to 320x200";
}

function vocGlyphBit7Label(ctrl2: number): string {
  return (ctrl2 & 0x20) !== 0 ? "internal invert" : "legacy upper-half";
}

function audioPitchExponent(mode: number): number {
  switch (mode & 0x03) {
    case 1:
      return 19;
    case 2:
      return 20;
    default:
      return 16;
  }
}

function audioPitchLabel(mode: number): string {
  return AUDIO_PITCH_LABELS[mode & 0x03] ?? AUDIO_PITCH_LABELS[0];
}

function audioWaveformLabel(ctrl: number): string {
  const waveBits = ctrl & AUDIO_WAVE_MASK;
  const count = countBits(waveBits);
  if (count === 0) {
    return "silence";
  }
  if (count > 1) {
    return "reserved-multi";
  }
  if ((waveBits & AUDIO_CTRL_TRI) !== 0) {
    return "triangle";
  }
  if ((waveBits & AUDIO_CTRL_SAW) !== 0) {
    return "saw";
  }
  if ((waveBits & AUDIO_CTRL_PULSE) !== 0) {
    return "pulse";
  }
  return "noise";
}

function audioEnvLabel(envState: number): string {
  return AUDIO_ENV_LABELS[envState] ?? "unknown";
}

function audioFilterModeLabel(filterCtrl: number): string {
  switch (filterCtrl & AUDIO_FILTER_MODE_MASK) {
    case 0x10:
      return "BP";
    case 0x20:
      return "HP";
    default:
      return "LP";
  }
}

function audioRouteLabels(route: number): string {
  const labels: string[] = [];
  if ((route & 0x01) !== 0) {
    labels.push("V1");
  }
  if ((route & 0x02) !== 0) {
    labels.push("V2");
  }
  if ((route & 0x04) !== 0) {
    labels.push("V3");
  }
  if ((route & 0x08) !== 0) {
    labels.push("V4");
  }
  return labels.length === 0 ? "none" : labels.join(", ");
}

function audioVoiceFrequencyHz(freq: number, pitchMode: number): number {
  return (freq * AUDIO_PITCH_CLOCK_HZ) / 2 ** audioPitchExponent(pitchMode);
}

function audioPulseWidthPercent(pulseWidth: number): number {
  return ((pulseWidth & 0x0fff) / 0x0fff) * 100;
}

function audioFilterCutoffHz(filterCutoff: number): number {
  const span = AUDIO_FILTER_CUTOFF_MAX_HZ - AUDIO_FILTER_CUTOFF_MIN_HZ;
  return AUDIO_FILTER_CUTOFF_MIN_HZ + Math.round((Math.min(filterCutoff, 0x07ff) * span) / 0x07ff);
}

function timerPrescalerLabel(prescaleSel: number): string {
  switch (prescaleSel & 0x03) {
    case 1:
      return "/8";
    case 2:
      return "/64";
    case 3:
      return "/1024";
    default:
      return "/1";
  }
}

function timerPrescalerValue(prescaleSel: number): number {
  switch (prescaleSel & 0x03) {
    case 1:
      return 8;
    case 2:
      return 64;
    case 3:
      return 1024;
    default:
      return 1;
  }
}

function timerTicksUntilUnderflow(count: number, prescaleSel: number, prescaleAccum: number, cascade: boolean): string {
  if (cascade) {
    return `${count} TA underflows`;
  }
  const prescaler = timerPrescalerValue(prescaleSel);
  return String(Math.max(0, count * prescaler - prescaleAccum));
}

function storageCommandLabel(command: number): string {
  return STORAGE_COMMAND_LABELS[command] ?? `0x${formatHexByte(command)}`;
}

function storageJobStateLabel(state: number): string {
  return STORAGE_JOB_STATE_LABELS[state] ?? `0x${formatHexByte(state)}`;
}

function storageStreamModeLabel(mode: number): string {
  return STORAGE_STREAM_MODE_LABELS[mode] ?? `0x${formatHexByte(mode)}`;
}

function storageErrorLabel(code: number): string {
  return STORAGE_ERROR_LABELS[code] ?? `0x${formatHexByte(code)}`;
}

function storageStatusFlags(status: number): string {
  const labels: string[] = [];
  if ((status & STORAGE_STATUS_BUSY) !== 0) {
    labels.push("busy");
  }
  if ((status & STORAGE_STATUS_DONE) !== 0) {
    labels.push("done");
  }
  if ((status & STORAGE_STATUS_ERR) !== 0) {
    labels.push("err");
  }
  if ((status & STORAGE_STATUS_MOUNTED) !== 0) {
    labels.push("mounted");
  }
  if ((status & STORAGE_STATUS_DIRTY) !== 0) {
    labels.push("dirty");
  }
  if ((status & STORAGE_STATUS_RO) !== 0) {
    labels.push("ro");
  }
  return labels.length === 0 ? "none" : labels.join(", ");
}

function iocSelectedLinesText(port: number, ddr: number): string {
  const selected: string[] = [];
  for (let index = 0; index < 8; index += 1) {
    const mask = 1 << index;
    if ((ddr & mask) !== 0 && (port & mask) === 0) {
      selected.push(String(index));
    }
  }
  return selected.length === 0 ? "none" : selected.join(", ");
}

function iocPressedMatrixSummary(keyMatrix: Uint8Array): string {
  const rows: string[] = [];
  keyMatrix.forEach((value, index) => {
    if (value === 0) {
      return;
    }
    const cols: number[] = [];
    for (let bit = 0; bit < 8; bit += 1) {
      if ((value & (1 << bit)) !== 0) {
        cols.push(bit);
      }
    }
    rows.push(`r${index}:${cols.join("/")}`);
  });
  return rows.length === 0 ? "none" : rows.join(", ");
}

function formatIocStickyTimestamp(frameNo: number, cycleCount: string): string {
  return `frame ${frameNo}, cycle ${cycleCount}`;
}

function formatIocStickyByte(valid: boolean, value: number, frameNo: number, cycleCount: string): string {
  if (!valid) {
    return "none";
  }
  return `${formatHexBytePrefixed(value)} @ ${formatIocStickyTimestamp(frameNo, cycleCount)}`;
}

function renderIocState(elements: DebuggerElements, ioc: IocSnapshot): void {
  const bankIndex = ioc.videoCtrl & 0x01;
  const bankBase = bankIndex === 0 ? 0x0000 : 0x8000;

  renderMachineGroups(elements.iocRegisters, [
    {
      title: "Core Registers",
      fields: [
        { label: "IOC_KBD_DATA", value: formatHexBytePrefixed(ioc.kbdData) },
        { label: "IOC_KBD_STAT", value: formatHexBytePrefixed(ioc.kbdStatus) },
        { label: "IOC_PRA", value: formatHexBytePrefixed(ioc.pra) },
        { label: "IOC_PRB", value: formatHexBytePrefixed(ioc.prb) },
        { label: "IOC_DDRA", value: formatHexBytePrefixed(ioc.ddra) },
        { label: "IOC_DDRB", value: formatHexBytePrefixed(ioc.ddrb) },
        { label: "IOC_VCTRL", value: formatHexBytePrefixed(ioc.videoCtrl) },
        { label: "IOC_ICR", value: formatHexBytePrefixed(ioc.icr) },
        { label: "IOC_CTRL", value: formatHexBytePrefixed(ioc.ctrl) },
        { label: "IOC_IDCAP", value: formatHexBytePrefixed(ioc.idcap) }
      ]
    },
    {
      title: "Inputs",
      fields: [
        { label: "JOY1", value: formatHexBytePrefixed(ioc.joy1) },
        { label: "JOY2", value: formatHexBytePrefixed(ioc.joy2) },
        { label: "MOUSE_DX", value: formatHexBytePrefixed(ioc.mouseDx & 0xff) },
        { label: "MOUSE_DY", value: formatHexBytePrefixed(ioc.mouseDy & 0xff) },
        { label: "MOUSE_BTN", value: formatHexBytePrefixed(ioc.mouseBtn) }
      ]
    }
  ]);

  renderMachineGroups(elements.iocInternal, [
    {
      title: "FIFO / IRQ",
      fields: [
        { label: "FIFO Head", value: String(ioc.fifoHead) },
        { label: "FIFO Tail", value: String(ioc.fifoTail) },
        { label: "FIFO Count", value: String(ioc.fifoCount) },
        { label: "FIFO Peak", value: String(ioc.stickyFifoCountPeak) },
        { label: "IER", value: formatHexBytePrefixed(ioc.ier) },
        { label: "IFR", value: formatHexBytePrefixed(ioc.ifr) },
        { label: "CTRL", value: formatHexBytePrefixed(ioc.ctrl) }
      ]
    },
    {
      title: "Key Matrix",
      fields: Array.from(ioc.keyMatrix, (value, index) => ({
        label: `Row ${index}`,
        value: formatHexBytePrefixed(value)
      }))
    }
  ]);

  renderMachineGroups(elements.iocDerived, [
    {
      title: "Keyboard / IRQ",
      fields: [
        { label: "Key Ready", value: formatYesNo((ioc.kbdStatus & IOC_KBD_STATUS_READY) !== 0) },
        { label: "FIFO Overflow", value: formatYesNo((ioc.kbdStatus & IOC_KBD_STATUS_OVERFLOW) !== 0) },
        { label: "IRQ Active", value: formatYesNo((ioc.kbdStatus & IOC_KBD_STATUS_IRQ) !== 0) },
        { label: "IRQ Summary", value: formatYesNo((ioc.icr & IOC_ICR_SET_MASK) !== 0) },
        { label: "Pressed Matrix", value: iocPressedMatrixSummary(ioc.keyMatrix) },
        { label: "Selected Rows", value: iocSelectedLinesText(ioc.pra, ioc.ddra) },
        { label: "Selected Cols", value: iocSelectedLinesText(ioc.prb, ioc.ddrb) }
      ]
    },
    {
      title: "Video / Mouse",
      fields: [
        { label: "Video Bank", value: String(bankIndex) },
        { label: "Bank Base", value: formatHexWordPrefixed(bankBase) },
        { label: "Mouse DX", value: String(ioc.mouseDx) },
        { label: "Mouse DY", value: String(ioc.mouseDy) },
        { label: "Mouse Buttons", value: formatHexBytePrefixed(ioc.mouseBtn) },
        { label: "FIFO Front", value: ioc.fifoCount === 0 ? "(empty)" : formatHexBytePrefixed(ioc.kbdData) }
      ]
    },
    {
      title: "Last Activity",
      fields: [
        {
          label: "Input Byte Push",
          value: formatIocStickyByte(
            ioc.stickyFifoPushValid,
            ioc.stickyFifoPushValue,
            ioc.stickyFifoPushFrameNo,
            ioc.stickyFifoPushCycleCount
          )
        },
        {
          label: "Input Byte Pop",
          value: formatIocStickyByte(
            ioc.stickyFifoPopValid,
            ioc.stickyFifoPopValue,
            ioc.stickyFifoPopFrameNo,
            ioc.stickyFifoPopCycleCount
          )
        },
        { label: "Matrix Event", value: ioc.stickyMatrixValid ? (ioc.stickyMatrixPressed ? "press" : "release") : "none" },
        { label: "Matrix Row", value: ioc.stickyMatrixValid ? String(ioc.stickyMatrixRow) : "-" },
        { label: "Matrix Col", value: ioc.stickyMatrixValid ? String(ioc.stickyMatrixCol) : "-" },
        { label: "Matrix Frame", value: ioc.stickyMatrixValid ? String(ioc.stickyMatrixFrameNo) : "-" },
        { label: "Matrix Cycle", value: ioc.stickyMatrixValid ? ioc.stickyMatrixCycleCount : "-" }
      ]
    }
  ]);
}

function renderVocState(elements: DebuggerElements, voc: VocSnapshot, ioc: IocSnapshot): void {
  const bankIndex = ioc.videoCtrl & 0x01;
  const bankBase = bankIndex === 0 ? 0x0000 : 0x8000;
  const screenBase = bankBase + (((voc.memPtr >> 4) & 0x0f) << 10);
  const colorBase = screenBase + 0x0400;
  const charBase = bankBase + 0x1000 + (((voc.memPtr >> 2) & 0x03) << 10);
  const bitmapBase = bankBase + 0x2000 + (voc.memPtr & 0x03) * 0x1000;
  const spritePtrBase = screenBase + SPRITE_PTR_OFFSET;
  const rasterVisible = voc.rasterLine >= VOC_VISIBLE_START && voc.rasterLine < VOC_VISIBLE_END;
  const visibleRow = rasterVisible ? String(voc.rasterLine - VOC_VISIBLE_START) : "n/a";
  const busOwner = VOC_BUS_OWNER_LABELS[voc.phiHighOwner] ?? `0x${formatHexByte(voc.phiHighOwner)}`;
  const highReason = VOC_HIGH_REASON_LABELS[voc.phiHighReason] ?? `0x${formatHexByte(voc.phiHighReason)}`;
  const modeLabel = vocModeLabel(voc.ctrl1, voc.ctrl2);
  const logicalResolution = vocLogicalResolutionLabel(voc.ctrl1, voc.ctrl2);

  renderMachineGroups(elements.vocRegisters, [
    {
      title: "Core Registers",
      fields: [
        { label: "VOC_CTRL1", value: formatHexBytePrefixed(voc.ctrl1) },
        { label: "VOC_RASTER", value: formatHexBytePrefixed(voc.raster) },
        { label: "VOC_CTRL2", value: formatHexBytePrefixed(voc.ctrl2) },
        { label: "VOC_MEMPTR", value: formatHexBytePrefixed(voc.memPtr) },
        { label: "VOC_IRQ_IFR", value: formatHexBytePrefixed(voc.irqIfr) },
        { label: "VOC_IRQ_IER", value: formatHexBytePrefixed(voc.irqIer) },
        { label: "VOC_BORDER_COLOR", value: formatHexBytePrefixed(voc.borderColor) },
        { label: "VOC_BG_COLOR", value: formatHexBytePrefixed(voc.bgColor) }
      ]
    },
    {
      title: "Sprites",
      subtitle: "position / color",
      fields: Array.from(voc.spriteX, (_, index) => ({
        label: `S${index}`,
        value: `${formatHexBytePrefixed(voc.spriteX[index])}, ${formatHexBytePrefixed(voc.spriteY[index])}, ${formatHexBytePrefixed(voc.spriteColor[index])}`
      }))
    },
    {
      title: "Sprite Pointers",
      fields: Array.from(voc.spritePtr, (value, index) => ({
        label: `S${index}`,
        value: formatHexBytePrefixed(value)
      }))
    },
    {
      title: "Palette",
      fields: Array.from(voc.palette, (entry, index) => ({
        label: `P${index}`,
        value: formatHexWordPrefixed(entry)
      }))
    }
  ]);

  renderMachineGroups(elements.vocInternal, [
    {
      title: "Raster / Fetch",
      fields: [
        { label: "Raster Line", value: String(voc.rasterLine) },
        { label: "Raster Cycle", value: String(voc.rasterCycle) },
        { label: "Frame No", value: String(voc.frameNo) },
        { label: "IRQ Compare", value: String(voc.rasterIrqCompare) },
        { label: "Badline Active", value: formatYesNo(voc.badlineActive) },
        { label: "Badline Row", value: String(voc.badlineRow) },
        { label: "GFX Line Valid", value: formatYesNo(voc.gfxLineValid) },
        { label: "GFX Visible Row", value: String(voc.gfxLineVisibleRow) },
        { label: "Visible Window", value: `${VOC_VISIBLE_START}..${VOC_VISIBLE_END - 1}` }
      ]
    },
    {
      title: "Bus Plan",
      fields: [
        { label: "Phi High Owner", value: busOwner },
        { label: "High Reason", value: highReason },
        { label: "Lookahead", value: String(voc.lookaheadCycles) },
        { label: "Next Fetch", value: String(voc.nextHighFetchDistance) },
        { label: "BA Low", value: formatYesNo(voc.baLow) },
        { label: "AEC Low", value: formatYesNo(voc.aecLow) }
      ]
    },
    {
      title: "Counters",
      fields: [
        { label: "Phi Low Video", value: voc.phiLowVideoCount },
        { label: "Phi High CPU", value: voc.phiHighCpuCount },
        { label: "Phi High Video", value: voc.phiHighVideoCount },
        { label: "Badlines", value: voc.badlineCount },
        { label: "Sprite DMA", value: voc.spriteDmaCount },
        { label: "Slot Fetches", value: voc.slotFetchCount },
        { label: "Stalls", value: voc.stallCount }
      ]
    }
  ]);

  renderMachineGroups(elements.vocDerived, [
    {
      title: "Display",
      fields: [
        { label: "Display Enabled", value: formatYesNo((voc.ctrl1 & 0x10) !== 0) },
        { label: "Mode", value: modeLabel },
        { label: "Logical Resolution", value: logicalResolution },
        { label: "Border Color", value: formatHexBytePrefixed(voc.borderColor) },
        { label: "BG Color", value: formatHexBytePrefixed(voc.bgColor) },
        { label: "X Scroll", value: String(voc.ctrl2 & 0x07) },
        { label: "Y Scroll", value: String(voc.ctrl1 & 0x07) },
        { label: "BMM / MCM / ECM", value: `${(voc.ctrl1 >> 5) & 1} / ${(voc.ctrl2 >> 4) & 1} / ${(voc.ctrl1 >> 6) & 1}` },
        { label: "Text Bit7", value: vocGlyphBit7Label(voc.ctrl2) },
        { label: "Raster Visible", value: formatYesNo(rasterVisible) },
        { label: "Visible Row", value: visibleRow },
        { label: "Badline", value: voc.badlineActive ? `row ${voc.badlineRow}` : "none" }
      ]
    },
    {
      title: "Addresses",
      fields: [
        { label: "Video Bank", value: String(bankIndex) },
        { label: "Bank Base", value: formatHexWordPrefixed(bankBase) },
        { label: "Screen Base", value: formatHexWordPrefixed(screenBase) },
        { label: "Color Base", value: `${formatHexWordPrefixed(colorBase)} (+500 bytes packed)` },
        { label: "Char Base", value: formatHexWordPrefixed(charBase) },
        { label: "Bitmap Base", value: formatHexWordPrefixed(bitmapBase) },
        { label: "Sprite Ptr Base", value: formatHexWordPrefixed(spritePtrBase) },
        { label: "Glyph Source", value: bankIndex === 0 ? "char ROM" : "banked RAM" },
        { label: "Text Matrix", value: `${TEXT_COLUMNS}x${TEXT_ROWS}` }
      ]
    },
    {
      title: "IRQ / Bus",
      fields: [
        { label: "Raster Compare", value: String(voc.rasterIrqCompare & 0x01ff) },
        { label: "IRQ Pending", value: vocIrqPendingText(voc.irqIfr) },
        { label: "IRQ Summary", value: formatYesNo((voc.irqIfr & 0x80) !== 0) },
        { label: "Current Bus Owner", value: busOwner },
        { label: "High-Phase Reason", value: highReason }
      ]
    }
  ]);
}

function renderAocState(elements: DebuggerElements, aoc: AocSnapshot): void {
  const voiceRawGroups = aoc.voices.map((voice, index) => ({
    title: `Voice ${index + 1}`,
    subtitle: "raw registers",
    fields: [
      { label: "FREQ", value: formatHexWordPrefixed(voice.freq) },
      { label: "PW", value: formatHexWordPrefixed(voice.pulseWidth) },
      { label: "CTRL", value: formatHexBytePrefixed(voice.ctrl) },
      { label: "AD", value: formatHexBytePrefixed(voice.ad) },
      { label: "SR", value: formatHexBytePrefixed(voice.sr) },
      { label: "AUX", value: formatHexBytePrefixed(voice.aux) }
    ]
  }));
  const voiceInternalGroups = aoc.voices.map((voice, index) => ({
    title: `Voice ${index + 1}`,
    subtitle: "internal",
    fields: [
      { label: "Phase Acc", value: formatHexDwordPrefixed(voice.phaseAcc) },
      { label: "Phase Frac", value: formatHexWordPrefixed(voice.phaseFracQ16) },
      { label: "Noise LFSR", value: formatHexDwordPrefixed(voice.noiseLfsr) },
      { label: "Env Q", value: formatHexDwordPrefixed(voice.envQ) },
      { label: "Env Counter", value: String(voice.envCounter) },
      { label: "Env Level", value: String(voice.envLevel) },
      { label: "Env State", value: audioEnvLabel(voice.envState) }
    ]
  }));
  const voiceDerivedGroups = aoc.voices.map((voice, index) => ({
    title: `Voice ${index + 1}`,
    subtitle: "derived",
    fields: [
      { label: "Waveform", value: audioWaveformLabel(voice.ctrl) },
      { label: "Frequency", value: formatHz(audioVoiceFrequencyHz(voice.freq, aoc.pitchMode)) },
      { label: "Pulse Width", value: formatPercent(audioPulseWidthPercent(voice.pulseWidth)) },
      { label: "Gate", value: formatYesNo((voice.ctrl & AUDIO_CTRL_GATE) !== 0) },
      { label: "Sync", value: formatYesNo((voice.ctrl & AUDIO_CTRL_SYNC) !== 0) },
      { label: "Ring", value: formatYesNo((voice.ctrl & AUDIO_CTRL_RING) !== 0) },
      { label: "Test", value: formatYesNo((voice.ctrl & AUDIO_CTRL_TEST) !== 0) },
      { label: "Voice Active", value: formatYesNo((voice.ctrl & AUDIO_CTRL_GATE) !== 0 || voice.envQ !== 0) },
      { label: "Rate x8", value: formatYesNo((voice.aux & AUDIO_AUX_RATE_X8) !== 0) }
    ]
  }));

  renderMachineGroups(elements.aocRegisters, [
    {
      title: "Global Registers",
      fields: [
        { label: "AUD_MODEVOL", value: formatHexBytePrefixed(aoc.modevol) },
        { label: "AUD_IRQ_EN", value: formatHexBytePrefixed(aoc.irqEn) },
        { label: "AUD_IRQ_IFR", value: formatHexBytePrefixed(aoc.irqIfr) },
        { label: "AUD_TICK", value: formatHexWordPrefixed(aoc.tickReload) },
        { label: "AUD_FC", value: formatHexWordPrefixed(aoc.filterCutoff) },
        { label: "AUD_FILT_CTRL", value: formatHexBytePrefixed(aoc.filterCtrl) },
        { label: "AUD_FILT_ROUTE", value: formatHexBytePrefixed(aoc.filterRoute) },
        { label: "AUD_OSC3_RO", value: formatHexBytePrefixed(aoc.osc3RoLatch) },
        { label: "AUD_ENV3_RO", value: formatHexBytePrefixed(aoc.env3RoLatch) },
        { label: "AUD_PITCH_CFG", value: formatHexBytePrefixed(aoc.pitchMode) }
      ]
    },
    ...voiceRawGroups
  ]);

  renderMachineGroups(elements.aocInternal, [
    {
      title: "Global Internal",
      fields: [
        { label: "Tick Counter", value: String(aoc.tickCounter) },
        { label: "Filter F Q16", value: formatHexDwordPrefixed(aoc.filterFQ16) },
        { label: "Filter Damp Q16", value: formatHexDwordPrefixed(aoc.filterDampQ16) },
        { label: "Filter Low Q16", value: formatHexDwordPrefixed(aoc.filterLowQ16) },
        { label: "Filter Band Q16", value: formatHexDwordPrefixed(aoc.filterBandQ16) },
        { label: "Last Sample", value: String(aoc.lastSample) },
        { label: "Batch Count", value: String(aoc.batchCount) },
        { label: "Synth Accum", value: String(aoc.synthCycleAccum) },
        { label: "Sample Count", value: aoc.sampleCount }
      ]
    },
    ...voiceInternalGroups
  ]);

  renderMachineGroups(elements.aocDerived, [
    {
      title: "Global Summary",
      fields: [
        { label: "Master Volume", value: String(aoc.modevol & 0x0f) },
        { label: "Pitch Mode", value: audioPitchLabel(aoc.pitchMode) },
        { label: "Pitch Clock", value: `${AUDIO_PITCH_CLOCK_HZ} Hz` },
        { label: "Source Rate", value: `${AUDIO_SOURCE_RATE_HZ} Hz` },
        { label: "Tick Running", value: formatYesNo(aoc.tickReload !== 0) },
        { label: "Tick Pending", value: formatYesNo((aoc.irqIfr & 0x01) !== 0) },
        { label: "Filter Cutoff", value: formatHz(audioFilterCutoffHz(aoc.filterCutoff)) },
        { label: "Filter Mode", value: audioFilterModeLabel(aoc.filterCtrl) },
        { label: "Resonance", value: String(aoc.filterCtrl & AUDIO_FILTER_RES_MASK) },
        { label: "Filter Route", value: audioRouteLabels(aoc.filterRoute) }
      ]
    },
    ...voiceDerivedGroups
  ]);
}

function renderTicState(elements: DebuggerElements, tic: TicSnapshot): void {
  const chipGroups = [
    { title: "TIC1", chip: tic.tic1, target: "IRQ" },
    { title: "TIC2", chip: tic.tic2, target: "NMI" }
  ];
  const registerGroups: MachineGroup[] = [];
  const internalGroups: MachineGroup[] = [];
  const derivedGroups: MachineGroup[] = [];

  for (const chipGroup of chipGroups) {
    registerGroups.push({
      title: `${chipGroup.title} Registers`,
      fields: [
        { label: "ICR", value: formatHexBytePrefixed(chipGroup.chip.icr) },
        { label: "CRA", value: formatHexBytePrefixed(chipGroup.chip.cra) },
        { label: "CRB", value: formatHexBytePrefixed(chipGroup.chip.crb) },
        { label: "TA Count", value: formatHexWordPrefixed(chipGroup.chip.timerA.count) },
        { label: "TB Count", value: formatHexWordPrefixed(chipGroup.chip.timerB.count) }
      ]
    });

    internalGroups.push({
      title: `${chipGroup.title} Timer A`,
      subtitle: "internal",
      fields: [
        { label: "Count", value: String(chipGroup.chip.timerA.count) },
        { label: "Latch", value: String(chipGroup.chip.timerA.latch) },
        { label: "Prescale Accum", value: String(chipGroup.chip.timerA.prescaleAccum) },
        { label: "IER", value: formatHexBytePrefixed(chipGroup.chip.timerA.ier) },
        { label: "IFR", value: formatHexBytePrefixed(chipGroup.chip.timerA.ifr) },
        { label: "CTRL", value: formatHexBytePrefixed(chipGroup.chip.timerA.ctrlReg) }
      ]
    });
    internalGroups.push({
      title: `${chipGroup.title} Timer B`,
      subtitle: "internal",
      fields: [
        { label: "Count", value: String(chipGroup.chip.timerB.count) },
        { label: "Latch", value: String(chipGroup.chip.timerB.latch) },
        { label: "Prescale Accum", value: String(chipGroup.chip.timerB.prescaleAccum) },
        { label: "IER", value: formatHexBytePrefixed(chipGroup.chip.timerB.ier) },
        { label: "IFR", value: formatHexBytePrefixed(chipGroup.chip.timerB.ifr) },
        { label: "CTRL", value: formatHexBytePrefixed(chipGroup.chip.timerB.ctrlReg) }
      ]
    });

    derivedGroups.push({
      title: `${chipGroup.title} Summary`,
      fields: [
        { label: "Line Target", value: chipGroup.target },
        {
          label: "IRQ Pending",
          value: formatYesNo(
            ((chipGroup.chip.timerA.ifr & chipGroup.chip.timerA.ier & TIC_IFR_UNDERFLOW) !== 0) ||
              ((chipGroup.chip.timerB.ifr & chipGroup.chip.timerB.ier & TIC_IFR_UNDERFLOW) !== 0)
          )
        }
      ]
    });
    derivedGroups.push({
      title: `${chipGroup.title} Timer A`,
      subtitle: "derived",
      fields: [
        { label: "Running", value: formatYesNo(chipGroup.chip.timerA.en) },
        { label: "One Shot", value: formatYesNo((chipGroup.chip.timerA.ctrlReg & TIC_CTRL_ONE_SHOT) !== 0) },
        { label: "Load Strobe", value: formatYesNo((chipGroup.chip.cra & TIC_CTRL_LOAD_STROBE) !== 0) },
        { label: "Prescaler", value: timerPrescalerLabel(chipGroup.chip.timerA.prescaleSel) },
        { label: "IRQ Enabled", value: formatYesNo((chipGroup.chip.timerA.ier & TIC_IFR_UNDERFLOW) !== 0) },
        { label: "IRQ Pending", value: formatYesNo((chipGroup.chip.timerA.ifr & TIC_IFR_UNDERFLOW) !== 0) },
        {
          label: "Ticks to Underflow",
          value: timerTicksUntilUnderflow(
            chipGroup.chip.timerA.count,
            chipGroup.chip.timerA.prescaleSel,
            chipGroup.chip.timerA.prescaleAccum,
            false
          )
        }
      ]
    });
    derivedGroups.push({
      title: `${chipGroup.title} Timer B`,
      subtitle: "derived",
      fields: [
        { label: "Running", value: formatYesNo(chipGroup.chip.timerB.en) },
        { label: "One Shot", value: formatYesNo((chipGroup.chip.timerB.ctrlReg & TIC_CTRL_ONE_SHOT) !== 0) },
        { label: "Load Strobe", value: formatYesNo((chipGroup.chip.crb & TIC_CTRL_LOAD_STROBE) !== 0) },
        { label: "Prescaler", value: timerPrescalerLabel(chipGroup.chip.timerB.prescaleSel) },
        {
          label: "Clock Source",
          value: (chipGroup.chip.timerB.ctrlReg & TIC_CTRL_TB_COUNT_TA_UNDERFLOW) !== 0 ? "timer-a-underflow" : "phi"
        },
        { label: "IRQ Enabled", value: formatYesNo((chipGroup.chip.timerB.ier & TIC_IFR_UNDERFLOW) !== 0) },
        { label: "IRQ Pending", value: formatYesNo((chipGroup.chip.timerB.ifr & TIC_IFR_UNDERFLOW) !== 0) },
        {
          label: "Ticks to Underflow",
          value: timerTicksUntilUnderflow(
            chipGroup.chip.timerB.count,
            chipGroup.chip.timerB.prescaleSel,
            chipGroup.chip.timerB.prescaleAccum,
            (chipGroup.chip.timerB.ctrlReg & TIC_CTRL_TB_COUNT_TA_UNDERFLOW) !== 0
          )
        }
      ]
    });
  }

  registerGroups.push({
    title: "TIC Ports",
    fields: [
      { label: "PRA", value: formatHexBytePrefixed(tic.pra) },
      { label: "PRB", value: formatHexBytePrefixed(tic.prb) },
      { label: "DDRA", value: formatHexBytePrefixed(tic.ddra) },
      { label: "DDRB", value: formatHexBytePrefixed(tic.ddrb) }
    ]
  });

  renderMachineGroups(elements.ticRegisters, registerGroups);
  renderMachineGroups(elements.ticInternal, internalGroups);
  renderMachineGroups(elements.ticDerived, derivedGroups);
}

function renderDicState(elements: DebuggerElements, dic: DicSnapshot): void {
  const selected = dic.units[dic.selectedUnit];
  const descriptorName = petsciiBytesToText(
    selected.descriptorBytes.slice(STORAGE_DESCRIPTOR_NAME_OFFSET, STORAGE_DESCRIPTOR_NAME_OFFSET + STORAGE_DESCRIPTOR_NAME_LENGTH)
  );
  const unitSummaryGroups = dic.units.map((unit, index) => ({
    title: `Unit ${index}`,
    subtitle: petsciiBytesToText(unit.labelBytes),
    fields: [
      { label: "Mounted", value: formatYesNo(unit.mounted) },
      { label: "Dirty", value: formatYesNo(unit.dirty) },
      { label: "Read Only", value: formatYesNo(unit.readOnly) },
      { label: "Free Blocks", value: String(unit.freeBlocks) }
    ]
  }));

  renderMachineGroups(elements.dicRegisters, [
    {
      title: `Selected Unit ${dic.selectedUnit}`,
      fields: [
        { label: "STG_UNIT", value: String(dic.selectedUnit) },
        { label: "STG_CMD", value: formatHexBytePrefixed(selected.mmioCmd) },
        { label: "STG_CTRL", value: formatHexBytePrefixed(selected.mmioCtrl) },
        { label: "STG_STATUS", value: formatHexBytePrefixed(selected.mmioStatus) },
        { label: "STG_ERR", value: formatHexBytePrefixed(selected.mmioErr) },
        { label: "STG_DESC", value: formatHexWordPrefixed((selected.mmioDescHi << 8) | selected.mmioDescLo) },
        { label: "STG_RES0", value: formatHexBytePrefixed(selected.mmioRes0) },
        { label: "STG_RES1", value: formatHexBytePrefixed(selected.mmioRes1) }
      ]
    },
    {
      title: "Descriptor Bytes",
      fields: Array.from(selected.descriptorBytes, (value, index) => ({
        label: index.toString(16).toUpperCase().padStart(2, "0"),
        value: formatHexBytePrefixed(value)
      }))
    }
  ]);

  renderMachineGroups(elements.dicInternal, [
    {
      title: "Stream / Job",
      fields: [
        { label: "Stream Mode", value: storageStreamModeLabel(selected.mmioStreamMode) },
        { label: "Stream Len", value: String(selected.mmioStreamLen) },
        { label: "Stream Pos", value: String(selected.mmioStreamPos) },
        { label: "Load Addr", value: formatHexWordPrefixed(selected.mmioStreamLoadAddr) },
        { label: "Overwrite", value: formatYesNo(selected.mmioStreamOverwrite) },
        { label: "Job State", value: storageJobStateLabel(selected.mmioJobState) },
        { label: "Job Cmd", value: storageCommandLabel(selected.mmioJobCmd) },
        { label: "Job Result", value: storageErrorLabel(selected.mmioJobResult) },
        { label: "Job Desc Addr", value: formatHexWordPrefixed(selected.mmioJobDescAddr) }
      ]
    },
    {
      title: "Descriptor / Cursor",
      fields: [
        { label: "Descriptor Valid", value: formatYesNo(selected.mmioDescValid) },
        { label: "Descriptor Dirty", value: formatYesNo(selected.mmioJobDescDirty) },
        { label: "Aux Name Valid", value: formatYesNo(selected.mmioAuxNameValid) },
        { label: "Stream Out Active", value: formatYesNo(selected.mmioDescStreamOutActive) },
        { label: "Stream Out Pos", value: String(selected.mmioDescStreamOutPos) },
        { label: "Dir Sector", value: String(selected.mmioStreamDirSector) },
        { label: "Dir Slot", value: String(selected.mmioStreamDirSlot) },
        { label: "Alloc Count", value: String(selected.mmioStreamAllocCount) },
        { label: "Alloc Index", value: String(selected.mmioStreamAllocIndex) },
        { label: "Alloc Written", value: String(selected.mmioStreamAllocWritten) }
      ]
    },
    ...unitSummaryGroups
  ]);

  renderMachineGroups(elements.dicDerived, [
    {
      title: "Selected Unit Summary",
      fields: [
        { label: "Command", value: storageCommandLabel(selected.mmioCmd) },
        { label: "Status Flags", value: storageStatusFlags(selected.mmioStatus) },
        { label: "Error", value: storageErrorLabel(selected.mmioErr) },
        { label: "Job State", value: storageJobStateLabel(selected.mmioJobState) },
        { label: "Stream Mode", value: storageStreamModeLabel(selected.mmioStreamMode) },
        { label: "Stream Progress", value: formatRatio(selected.mmioStreamPos, selected.mmioStreamLen) },
        {
          label: "Expected/Written",
          value: `${selected.mmioStreamWrittenBytes}/${selected.mmioStreamExpectedBytes}`
        },
        { label: "Descriptor Name", value: descriptorName },
        {
          label: "Buffer Addr",
          value: formatHexWordPrefixed(descriptorU16(selected.descriptorBytes, STORAGE_DESCRIPTOR_BUFFER_ADDR_OFFSET))
        },
        {
          label: "Buffer Len",
          value: String(descriptorU16(selected.descriptorBytes, STORAGE_DESCRIPTOR_BUFFER_LEN_OFFSET))
        },
        {
          label: "Xfer Len",
          value: String(descriptorU16(selected.descriptorBytes, STORAGE_DESCRIPTOR_XFER_LEN_OFFSET))
        },
        { label: "Dir Cursor", value: `sector ${selected.mmioStreamDirSector}, slot ${selected.mmioStreamDirSlot}` }
      ]
    },
    ...unitSummaryGroups
  ]);
}

export function renderMachineState(elements: DebuggerElements, snapshots: MachineSnapshots): void {
  renderIocState(elements, snapshots.ioc);
  renderVocState(elements, snapshots.voc, snapshots.ioc);
  renderAocState(elements, snapshots.aoc);
  renderTicState(elements, snapshots.tic);
  renderDicState(elements, snapshots.dic);
}

export function renderSnapshot(elements: DebuggerElements, snapshot: CoreSnapshot): void {
  elements.registerPc.textContent = formatHexWord(snapshot.pc);
  elements.registerA.textContent = formatHexByte(snapshot.a);
  elements.registerX.textContent = formatHexByte(snapshot.x);
  elements.registerY.textContent = formatHexByte(snapshot.y);
  elements.registerSp.textContent = formatHexByte(snapshot.sp);
  elements.registerP.textContent = formatHexByte(snapshot.p);
  elements.registerIrq.textContent = snapshot.irqLine ? "1" : "0";
  elements.registerNmi.textContent = snapshot.nmiLine ? "1" : "0";
  elements.phaseValue.textContent = formatPhaseLabel(snapshot.phase);
  renderFlag(elements.flagN, (snapshot.p & FLAG_NEGATIVE) !== 0);
  renderFlag(elements.flagV, (snapshot.p & FLAG_OVERFLOW) !== 0);
  renderFlag(elements.flagI, (snapshot.p & FLAG_INTERRUPT_DISABLE) !== 0);
  renderFlag(elements.flagZ, (snapshot.p & FLAG_ZERO) !== 0);
  renderFlag(elements.flagC, (snapshot.p & FLAG_CARRY) !== 0);
  renderFlag(elements.flagFetch, snapshot.instrBoundary);
  renderFlag(elements.flagHalt, snapshot.halted);
}
